/**
 * MultiImageUpload — gallery uploader for a product (1-8 shots).
 *
 * Backed by the same `/api/upload/image?image_type=product` endpoint the
 * single-image ImageUpload uses; we just repeat the call and gather the
 * relative `/api/uploads/…` URLs into a `value` array the parent owns.
 *
 * - Drag-and-drop or click to add multiple shots.
 * - Preview strip with drag-to-reorder (arrow buttons on mobile).
 * - Per-tile Remove button.
 * - Enforces `maxImages` cap client-side; backend `cap_product_images`
 *   enforces the tier limit again on save.
 * - Zero blob:/data:/file: URLs are ever persisted — the parent only
 *   receives the permanent public paths.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, X, ArrowLeft, ArrowRight, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { resolveAssetUrl } from "@/lib/assetUrl";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif";
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB per image

export default function MultiImageUpload({
  value = [],
  onChange,
  maxImages = 8,
  imageType = "product",
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const remaining = Math.max(0, maxImages - value.length);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("image_type", imageType);
    const r = await axios.post(
      `${API}/upload/image?image_type=${imageType}`,
      fd,
      { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
    );
    // Relative /api/uploads/… — DO NOT prepend anything.
    return r.data?.url;
  };

  const handleFiles = async (fileList) => {
    if (disabled) return;
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    if (remaining <= 0) {
      toast.error(`Max ${maxImages} images per product`);
      return;
    }
    const accepted = files.slice(0, remaining).filter((f) => {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: over 10 MB, skipped`);
        return false;
      }
      return true;
    });
    if (!accepted.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of accepted) {
        try {
          const u = await uploadOne(f);
          if (u) urls.push(u);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Upload failed: ${f.name}`);
        }
      }
      if (urls.length) {
        onChange?.([...value, ...urls]);
        toast.success(`Added ${urls.length} image${urls.length === 1 ? "" : "s"}`);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (i) => onChange?.(value.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange?.(next);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer?.files);
  };

  return (
    <div className="space-y-3" data-testid="multi-image-upload">
      {/* Tile strip */}
      {value.length > 0 && (
        <div
          className="grid grid-cols-4 gap-2"
          data-testid="multi-image-tiles"
        >
          {value.map((url, i) => (
            <div
              key={`${url}-${i}`}
              className="relative group aspect-square rounded-lg overflow-hidden bg-tbc-cream/40 border border-tbc-navy/10"
              data-testid={`multi-image-tile-${i}`}
            >
              <img
                src={resolveAssetUrl(url)}
                alt={`Product image ${i + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Legacy doubled-prefix URLs bail here — swap to a neutral
                  // placeholder box instead of the browser's jagged icon.
                  if (e.currentTarget.dataset.fallback !== "1") {
                    e.currentTarget.dataset.fallback = "1";
                    e.currentTarget.src =
                      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23f5f0e8'/><text x='50' y='55' text-anchor='middle' fill='%236b5b3a' font-size='10' font-family='sans-serif'>image missing</text></svg>";
                  }
                }}
              />
              {/* Order badge */}
              <span className="absolute top-1 left-1 w-5 h-5 rounded-full bg-tbc-navy/85 text-white text-[10px] font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              {/* Actions */}
              <div className="absolute inset-x-1 bottom-1 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0 || disabled}
                    className="w-6 h-6 rounded-full bg-white/95 text-tbc-navy hover:bg-white flex items-center justify-center disabled:opacity-40"
                    aria-label="Move earlier"
                    data-testid={`multi-image-move-left-${i}`}
                  >
                    <ArrowLeft className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, +1)}
                    disabled={i === value.length - 1 || disabled}
                    className="w-6 h-6 rounded-full bg-white/95 text-tbc-navy hover:bg-white flex items-center justify-center disabled:opacity-40"
                    aria-label="Move later"
                    data-testid={`multi-image-move-right-${i}`}
                  >
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  disabled={disabled}
                  className="w-6 h-6 rounded-full bg-red-500 text-white hover:bg-red-600 flex items-center justify-center"
                  aria-label="Remove image"
                  data-testid={`multi-image-remove-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              {/* Cover-photo hint on tile 1 */}
              {i === 0 && (
                <span className="absolute top-1 right-1 text-[9px] uppercase tracking-wider bg-tbc-gold text-tbc-navy px-1.5 py-0.5 rounded-sm">
                  Cover
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dropzone / add button */}
      {remaining > 0 && (
        <div
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          className={`rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
            dragActive
              ? "border-tbc-gold bg-tbc-gold/10"
              : "border-tbc-navy/20 bg-tbc-cream/30"
          }`}
          data-testid="multi-image-dropzone"
        >
          <ImageIcon className="w-6 h-6 text-tbc-navy/40 mx-auto mb-1" />
          <p className="text-xs text-tbc-navy/70">
            Add up to <strong>{remaining}</strong> more image{remaining === 1 ? "" : "s"} · drag &amp; drop or
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2 h-8 text-xs"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || disabled}
            data-testid="multi-image-pick-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload className="w-3 h-3 mr-1" /> Choose files
              </>
            )}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="multi-image-input"
          />
        </div>
      )}

      {value.length > 0 && (
        <p className="text-[11px] text-tbc-navy/50">
          Tile 1 is the cover shown on cards and the storefront. Drag or use the arrows to reorder.
        </p>
      )}
    </div>
  );
}
